import * as PropTypes from 'prop-types';

import {Permissions, PERMISSIONS_SHAPE} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import * as SampleConstants from 'constants/SampleConstants';
import UserConstants, {User} from 'constants/UserConstants';
import * as FileConstants from 'folder-items/FileConstants';
import {LegacyAssaySchemaType} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {EntrySummary} from 'notebook/ELNConstants';
import * as OrganizationConstants from 'orgs/OrganizationConstants';
import * as SchemaFieldConstants from 'schemas/fields/SchemaFieldConstants';
import {Schema} from 'schemas/SchemaConstants';
import {MinimalEntity} from 'ui-library/entity-input/EntityInputConstants';
import * as NameUtil from 'util/NameUtil';
import {
  STAGE_RUN_STATUS,
  STAGE_RUN_STATUS_COMPLETED,
  STAGE_RUN_STATUS_DISCARDED,
} from 'workflows/constants/StageRunConstants';
import {SAMPLE_GROUP_SCHEMA_SHAPE, SampleGroupSchema, WorkflowSummary} from 'workflows/WorkflowConstants';

import {FulfillmentStatus, RequestFulfillment} from './fulfillments/FulfillmentConstants';
import {RequestDetailsQuery} from './request-view/__generated/RequestDetailsQuery';

export type RequestDetailsRequestGraphQL = ExtractByTypename<RequestDetailsQuery['request'], 'RequestNode'>;
export type RequestDetailsSampleGroupGraphQL = RequestDetailsRequestGraphQL['sampleGroups'][number];
export type RequestDetailsSampleGroupSampleGraphQL = RequestDetailsSampleGroupGraphQL['samples'][number];
export type RequestDetailsSampleGroupSchemaGraphQL = NonNullable<
  RequestDetailsRequestGraphQL['schema']['sampleGroupSchema']
>;
export type RequestDetailsTaskGraphQL = RequestDetailsRequestGraphQL['tasks'][number];
export type RequestDetailsFulfillmentGraphQL = RequestDetailsRequestGraphQL['fulfillments'][number];

type Organization = OrganizationConstants.Organization;
type Tag = FileConstants.Tag;
type Team = OrganizationConstants.Team;

/**
 * Returns the formatted version of the name of a legacy request object, based on whether the REQUESTS_V2
 * flag is on.
 * @param plural Whether to pluralize the object name
 * @param lowerCase Whether to convert to object name to all lowercase letters
 * @param capwords Whether to capitalize the first letter of every word in the object name
 */
export function getLegacyRequestObjectName(
  {plural, lowerCase, capwords}: {plural?: boolean; lowerCase?: boolean; capwords?: boolean} = {
    plural: false,
    lowerCase: false,
    capwords: false,
  }
): string {
  const REQUEST_OBJECT_NAME = 'Request';
  const REQUEST_OBJECT_LEGACY_NAME = 'Legacy request';
  let requestObjectName = BenchlingConfig.REQUESTS_V2_ENABLE
    ? REQUEST_OBJECT_LEGACY_NAME
    : REQUEST_OBJECT_NAME;
  if (plural) {
    requestObjectName = pluralize(2, requestObjectName);
  }
  if (lowerCase) {
    requestObjectName = requestObjectName.toLowerCase();
  }
  if (capwords) {
    requestObjectName = NameUtil.capwords(requestObjectName);
  }
  return requestObjectName;
}

// Keep in sync with server-side RequestStatus enum
export enum RequestStatus {
  REQUESTED = 'REQUESTED',
  SCHEDULED = 'SCHEDULED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

export const REQUEST_STATUSES = [
  RequestStatus.REQUESTED,
  RequestStatus.SCHEDULED,
  RequestStatus.IN_PROGRESS,
  RequestStatus.COMPLETED,
  RequestStatus.CANCELLED,
];
export const REQUEST_STATUS_NAMES: {[status in RequestStatus]: string} = {
  [RequestStatus.REQUESTED]: 'Requested',
  [RequestStatus.SCHEDULED]: 'Scheduled',
  [RequestStatus.IN_PROGRESS]: 'In Progress',
  [RequestStatus.COMPLETED]: 'Completed',
  [RequestStatus.CANCELLED]: 'Cancelled',
};

export interface RequestAssigneeUser {
  user: User;
  team: null;
}

export interface RequestAssigneeTeam {
  user: null;
  team: Team;
}

export type RequestAssignee = RequestAssigneeUser | RequestAssigneeTeam;

interface MinimalRequestAssigneeUser {
  user: MinimalEntity;
  team: null;
}

interface MinimalRequestAssigneeTeam {
  user: null;
  team: MinimalEntity;
}

export type MinimalRequestAssignee = MinimalRequestAssigneeUser | MinimalRequestAssigneeTeam;

export const REQUEST_ASSIGNEE_SHAPE = PropTypes.shape({
  team: OrganizationConstants.TEAM,
  user: PropTypes.shape(UserConstants.USER_SHAPE),
});

export const REQUEST_SUMMARY_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
});

export interface Request {
  id: string;
  // Request cannot be archived but field is needed for SmartChip (similar to WorklistSummary)
  archiveRecord?: undefined;
  assignees: Array<RequestAssignee>;
  createdAt: string;
  creator: User;
  requestor: User;
  name: string;
  projectId: string;
  requestStatus: RequestStatus;
  scheduledOn: string | null;
  schemaId: string;
  tags: Array<Tag>;
  permissions: Permissions;
}

export const REQUEST = {
  id: PropTypes.string.isRequired,
  assignees: PropTypes.arrayOf(REQUEST_ASSIGNEE_SHAPE.isRequired).isRequired,
  createdAt: PropTypes.string.isRequired,
  creator: PropTypes.object.isRequired,
  requestor: PropTypes.object,
  name: PropTypes.string.isRequired,
  projectId: PropTypes.string,
  requestStatus: PropTypes.oneOf(REQUEST_STATUSES).isRequired,
  scheduledOn: PropTypes.string,
  schemaId: PropTypes.string.isRequired,
  tags: PropTypes.arrayOf(FileConstants.TAG_SHAPE).isRequired,
  permissions: PERMISSIONS_SHAPE.isRequired,
};

export const REQUEST_SHAPE = PropTypes.shape(REQUEST);

export interface RequestResponseSample {
  batch: SampleConstants.Batch | null;
  bioentity: FileConstants.BioentitySummary | null;
  status: STAGE_RUN_STATUS.COMPLETED | STAGE_RUN_STATUS.DISCARDED;
}
export interface NormalizedRequestResponseSample {
  batchId: string | null;
  bioentityId: string | null;
  status: STAGE_RUN_STATUS.COMPLETED | STAGE_RUN_STATUS.DISCARDED;
}

export const REQUEST_RESPONSE_SAMPLE_SHAPE = PropTypes.shape({
  batch: SampleConstants.BATCH_SHAPE,
  bioentity: FileConstants.FILE_SUMMARY_SHAPE,
  status: PropTypes.oneOf([STAGE_RUN_STATUS_COMPLETED, STAGE_RUN_STATUS_DISCARDED]).isRequired,
});

export interface RequestResponse {
  samples: Array<RequestResponseSample>;
  fulfillments: Array<RequestFulfillment>;
  entrySummariesById: {[entryId: string]: EntrySummary};
  workflowSummaries: Array<WorkflowSummary>;
}

export const REQUEST_RESPONSE_SHAPE = PropTypes.shape({
  samples: PropTypes.arrayOf(REQUEST_RESPONSE_SAMPLE_SHAPE.isRequired).isRequired,
});

export interface RequestSchema extends Schema {
  schemaType: LegacyAssaySchemaType;
  derivedParent: null;
  publishedDataTableColumns: null;
  name: string;
  prefix: string;
  sqlIdentifier: string;
  organization: Organization;
  requestTaskSchemaIds: Array<string>;
  sampleGroupSchema: SampleGroupSchema | null;
  requestTemplateIds: Array<string>;
}

export const REQUEST_SCHEMA_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  fields: PropTypes.arrayOf(SchemaFieldConstants.SCHEMA_FIELD_SHAPE.isRequired).isRequired,
  name: PropTypes.string.isRequired,
  prefix: PropTypes.string.isRequired,
  sqlIdentifier: PropTypes.string.isRequired,
  organization: OrganizationConstants.ORGANIZATION.isRequired,
  requestTaskSchemaIds: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  sampleGroupSchema: SAMPLE_GROUP_SCHEMA_SHAPE,
});

export const SCHEMA_ID_REQUEST_SCHEMA_ALL = 'SCHEMA_ID_REQUEST_SCHEMA_ALL';
export const SCHEMA_OPTION_REQUEST_SCHEMA_ALL: {id: typeof SCHEMA_ID_REQUEST_SCHEMA_ALL; name: string} = {
  id: SCHEMA_ID_REQUEST_SCHEMA_ALL,
  name: 'All',
};

export interface RequestFullfillableStatuses {
  [sampleGroupId: string]: {[requestTaskId: string]: FulfillmentStatus};
}

export const REQUEST_FULFILLABLE_STATUSES_SHAPE = PropTypes.objectOf(
  PropTypes.objectOf(PropTypes.string.isRequired).isRequired
);

export const REQUEST_TABLE_ICON = 'input';
export const REQUEST_TABLE_TITLE = 'Inputs';

export const RequestPreferences = {
  SELECT_REQUEST_SAMPLE_FORM_DEFAULT_INPUT_TYPE: 'SelectRequestSampleForm.defaultInputType',
  LAST_USED_REQUEST_PROJECT: 'requests.create.LAST_USED_PROJECT',
};
